import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { ClipboardList, Plus } from "lucide-react";
import { toast } from "sonner";

/**
 * Coach's saved-program library. Extracted from CoachDashboard.jsx so the
 * dashboard file stays under ~700 lines and this widget can be reused (e.g.
 * from a future "Templates" page).
 *
 * Props:
 *  - programs        : array of program docs from `/coaching/programs`
 *  - onReuse(prog)   : parent handler that opens the builder pre-filled
 *  - onReload()      : re-fetch programs after tag edits
 */
export default function ProgramLibrary({ programs, onReuse, onReload }) {
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [editingTagsFor, setEditingTagsFor] = useState(null);
  const [tagDraft, setTagDraft] = useState("");

  const allTags = useMemo(() => {
    const s = new Set();
    (programs || []).forEach((p) => (p.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [programs]);

  const filteredPrograms = useMemo(() => {
    if (!activeTagFilter) return programs || [];
    return (programs || []).filter((p) => (p.tags || []).includes(activeTagFilter));
  }, [programs, activeTagFilter]);

  const saveTags = async (templateId, tags) => {
    try {
      await api.patch(`/coaching/programs/${templateId}/tags`, { tags });
      toast.success("Tags saved");
      setEditingTagsFor(null);
      setTagDraft("");
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save tags");
    }
  };

  if (!programs || programs.length === 0) return null;

  return (
    <div className="obs-card" data-testid="program-library">
      <div className="flex items-center gap-3 mb-3">
        <ClipboardList className="h-5 w-5 text-blaze" />
        <div className="flex-1">
          <div className="overline">Programs</div>
          <h3 className="font-heading text-2xl uppercase">Your library</h3>
        </div>
      </div>

      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="program-tag-filters">
          <button
            type="button"
            onClick={() => setActiveTagFilter(null)}
            className={`text-[10px] uppercase tracking-widest px-3 py-1 border rounded-full ${
              !activeTagFilter
                ? "bg-blaze text-obsidian-900 border-blaze"
                : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
            }`}
            data-testid="program-filter-all"
          >
            All · {programs.length}
          </button>
          {allTags.map((t) => {
            const count = programs.filter((p) => (p.tags || []).includes(t)).length;
            const active = activeTagFilter === t;
            return (
              <button
                type="button"
                key={t}
                onClick={() => setActiveTagFilter(active ? null : t)}
                className={`text-[10px] uppercase tracking-widest px-3 py-1 border rounded-full ${
                  active
                    ? "bg-blaze text-obsidian-900 border-blaze"
                    : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-blaze"
                }`}
                data-testid={`program-filter-${t}`}
              >
                {t} · {count}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filteredPrograms.map((p) => {
          const exList = p.data?.exercises || [];
          const expanded = expandedProgram === p.id;
          const editingTags = editingTagsFor === p.id;
          return (
            <div
              key={p.id}
              className="border border-obsidian-600 rounded-sm p-3"
              data-testid={`program-card-${p.id}`}
            >
              <div className="font-heading text-lg uppercase">{p.name}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
                {exList.length} exercises · sent to {p.assigned_count} ·{" "}
                {new Date(p.created_at).toLocaleDateString()}
              </div>
              {p.data?.notes && (
                <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{p.data.notes}</div>
              )}

              {editingTags ? (
                <div className="mt-3 flex flex-wrap gap-1.5 items-center border border-obsidian-600 rounded-sm p-2 bg-obsidian-900/40">
                  {tagDraft
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean)
                    .map((t, i) => (
                      <span
                        key={i}
                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-blaze/40 text-blaze rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  <input
                    autoFocus
                    value={tagDraft}
                    onChange={(e) => setTagDraft(e.target.value)}
                    placeholder="push, upper, hypertrophy…"
                    className="flex-1 min-w-[10rem] bg-transparent border-none focus:outline-none text-xs text-zinc-100 placeholder-zinc-600"
                    data-testid={`program-tag-input-${p.id}`}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      saveTags(
                        p.id,
                        tagDraft
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean),
                      )
                    }
                    className="blaze-btn px-2 py-1 text-[10px] uppercase tracking-widest"
                    data-testid={`program-tag-save-${p.id}`}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTagsFor(null);
                      setTagDraft("");
                    }}
                    className="ghost-btn px-2 py-1 text-[10px] uppercase tracking-widest"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                  {(p.tags || []).map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => setActiveTagFilter(activeTagFilter === t ? null : t)}
                      className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-blaze/40 text-blaze rounded-full hover:bg-blaze/10"
                    >
                      {t}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTagsFor(p.id);
                      setTagDraft((p.tags || []).join(", "));
                    }}
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-obsidian-600 text-zinc-500 rounded-full hover:border-blaze hover:text-blaze inline-flex items-center gap-1"
                    data-testid={`program-tag-edit-${p.id}`}
                  >
                    <Plus className="h-2.5 w-2.5" /> Tag
                  </button>
                </div>
              )}

              {expanded && exList.length > 0 && (
                <ul
                  data-testid={`program-preview-${p.id}`}
                  className="mt-3 space-y-1 text-xs text-zinc-300 border-t border-obsidian-600 pt-3"
                >
                  {exList.map((ex, i) => {
                    const topSet = Array.isArray(ex.sets) ? ex.sets[0] : null;
                    return (
                      <li key={i} className="flex items-baseline gap-2">
                        <span className="text-zinc-600 font-data">{i + 1}.</span>
                        <span className="flex-1 truncate">{ex.name}</span>
                        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
                          {Array.isArray(ex.sets)
                            ? `${ex.sets.length}×${topSet?.reps ?? "?"}`
                            : `${ex.sets}×${ex.reps ?? "?"}`}
                          {topSet?.weight_kg ? ` @ ${topSet.weight_kg}kg` : ""}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setExpandedProgram(expanded ? null : p.id)}
                  className="ghost-btn px-3 py-1.5 text-[11px] uppercase tracking-widest"
                  data-testid={`program-preview-toggle-${p.id}`}
                >
                  {expanded ? "Hide" : "Preview"}
                </button>
                <button
                  type="button"
                  onClick={() => onReuse?.(p)}
                  className="blaze-btn px-3 py-1.5 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
                  data-testid={`program-reuse-${p.id}`}
                >
                  <ClipboardList className="h-3 w-3" /> Reuse
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredPrograms.length === 0 && (
        <div className="text-xs text-zinc-500 mt-3">
          No programs tagged &quot;{activeTagFilter}&quot;.{" "}
          <button
            type="button"
            className="underline"
            onClick={() => setActiveTagFilter(null)}
          >
            Clear filter
          </button>
          .
        </div>
      )}
    </div>
  );
}
